import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

function formatTime(s: number) {
  if (!isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60)
    .toString()
    .padStart(2, '0');
  return `${m}:${r}`;
}

export function VideoControlsMinimal({
  videoRef,
}: {
  videoRef: React.RefObject<HTMLVideoElement | null>;
}) {
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);

  // ...
  const pctMV = useMotionValue(0); // 0..1 cru
  const pct = useSpring(pctMV, { stiffness: 200, damping: 20, mass: 0.6 });

  // mapeia para string "XX%"
  const pctPercent = useTransform(pct, (r) => `${r * 100}%`);
  const knobX = useTransform(pct, (r) => `calc(${r * 100}% - 6px)`); // knob 12px => -6px

  // atualiza barra conforme tempo
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const onLoaded = () => {
      setDuration(v.duration || 0);
      // tentar tocar com áudio — já houve gesto de clique ao abrir o modal
      v.muted = false;
      v.play()
        .then(() => setPlaying(true))
        .catch(() => {});
    };
    const onTime = () => {
      setCurrent(v.currentTime || 0);
      if (v.duration) pctMV.set((v.currentTime || 0) / v.duration);
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onVol = () => setMuted(v.muted);

    v.addEventListener('loadedmetadata', onLoaded);
    v.addEventListener('canplay', onLoaded);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('volumechange', onVol);

    return () => {
      v.removeEventListener('loadedmetadata', onLoaded);
      v.removeEventListener('canplay', onLoaded);
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('volumechange', onVol);
    };
  }, [videoRef, pctMV]);

  // play/pause
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  // mute
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  // scrub (click/drag) na barra
  const barRef = useRef<HTMLDivElement | null>(null);
  const isDragging = useRef(false);

  const seekAt = (clientX: number) => {
    const v = videoRef.current;
    const bar = barRef.current;
    if (!v || !bar || !duration) return;
    const rect = bar.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    const ratio = x / rect.width;
    v.currentTime = ratio * duration;
    pctMV.set(ratio);
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      seekAt(e.clientX);
    };
    const onUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    if (isDragging.current) {
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    }
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [duration, videoRef, pctMV]);

  return (
    <div className="absolute lg:w-[99rem] w-[90%] h-[2rem] inset-x-0 bottom-[4.5rem] z-20 px-6 pb-[3.5rem] pt-2 pointer-events-none -translate-x-1/2 left-1/2 mix-blend-difference">
      <div className="flex items-center gap-4 text-white/90 pointer-events-auto">
        {/* Play/Pause */}
        <button onClick={togglePlay} className="text-sm px-2 py-1 rounded">
          {playing ? (
            <svg
              className="w-[2.5rem] h-[2.5rem]"
              xmlns="http://www.w3.org/2000/svg"
              width="9"
              height="16"
              fill="none"
              viewBox="0 0 9 16"
            >
              <path fill="#fff" d="M0 .5h3v15H0zM6 .5h3v15H6z"></path>
            </svg>
          ) : (
            <svg
              className="w-[2.5rem] h-[2.5rem]"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 12 15"
              aria-hidden="true"
            >
              <path fill="#fff" d="M12 7.5 0 .572v13.856z" />
            </svg>
          )}
        </button>

        {/* Barra de progresso */}
        <div
          ref={barRef}
          className="relative flex-1 py-[2rem]   cursor-pointer select-none"
          onMouseDown={(e) => {
            isDragging.current = true;
            seekAt(e.clientX);
          }}
          onClick={(e) => seekAt(e.clientX)}
        >
          <div className="h-[0.25rem] relative rounded-full bg-white/30 cursor-pointer select-none">
            {/* trilho preenchido (animado) */}
            <motion.div
              className="absolute left-0 top-0 h-full rounded-full bg-white"
              style={{ width: pctPercent }}
            />
          </div>
        </div>

        {/* Mute */}
        <button onClick={toggleMute} className="text-xs px-2 py-1 ">
          {muted ? (
            <svg
              className="w-[2.5rem] h-[2.5rem]"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="none"
              viewBox="0 0 16 16"
            >
              <path
                fill="#fff"
                fillOpacity="0.01"
                d="M0 0h16v16H0z"
                style={{ mixBlendMode: 'multiply' }}
              ></path>
              <path
                fill="#fff"
                d="m15.5 6.205-.705-.705L13 7.295 11.205 5.5l-.705.705L12.295 8 10.5 9.795l.705.705L13 8.705l1.795 1.795.705-.705L13.705 8zM9 15a.5.5 0 0 1-.355-.15L4.835 11H1.5a.5.5 0 0 1-.5-.5v-5a.5.5 0 0 1 .5-.5h3.335l3.81-3.85a.5.5 0 0 1 .705 0 .5.5 0 0 1 .15.35v13a.5.5 0 0 1-.5.5"
              ></path>
            </svg>
          ) : (
            <svg
              className="w-[2.5rem] h-[2.5rem]"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="none"
              viewBox="0 0 16 16"
            >
              <path
                fill="#fff"
                fillOpacity="0.01"
                d="M0 0h16v16H0z"
                style={{ mixBlendMode: 'multiply' }}
              ></path>
              <path
                fill="#fff"
                d="m13.58 4.04-.765.645a5 5 0 0 1-.145 6.615l.735.7a6 6 0 0 0 .175-7.94z"
              ></path>
              <path
                fill="#fff"
                d="M10.79 6a3 3 0 0 1-.09 3.97l.735.68a4 4 0 0 0 .115-5.295zM9 15a.5.5 0 0 1-.355-.15L4.835 11H1.5a.5.5 0 0 1-.5-.5v-5a.5.5 0 0 1 .5-.5h3.335l3.81-3.85a.5.5 0 0 1 .855.35v13a.5.5 0 0 1-.5.5"
              ></path>
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}
